import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Coins, LogOut, UserCircle2, Shield, Link2, KeyRound, Megaphone, Sun, Moon, Monitor } from "lucide-react";
import ChangePasswordModal from "./ChangePasswordModal";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useDarkMode } from "../context/DarkModeContext";

export default function Header({ variant = "marketing" }) {
  var auth          = useAuth();
  var user          = auth.user;
  var logout        = auth.logout;
  var themeCtx      = useTheme();
  var theme         = themeCtx.theme;
  var darkMode      = useDarkMode();
  var isDark        = darkMode.isDark;
  var toggleMode    = darkMode.toggleMode;
  var resetToSystem = darkMode.resetToSystem;

  var navigate      = useNavigate();
  var [changePwOpen, setChangePwOpen] = useState(false);
  var [modeMenu,     setModeMenu]     = useState(false);

  var handleLogout = async function () {
    await logout();
    navigate("/");
  };

  // Header bg adapts to mode
  var headerStyle = {
    background: isDark
      ? "rgba(3,3,3,0.85)"
      : "rgba(250,250,250,0.92)",
    borderColor: isDark
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.08)",
  };

  var textClass = isDark ? "text-zinc-300" : "text-zinc-600";
  var btnBorder = isDark ? "border-white/10 hover:border-white/20" : "border-black/10 hover:border-black/20";
  var btnText   = isDark ? "text-zinc-300 hover:text-white"        : "text-zinc-600 hover:text-black";

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b saturate-150 transition-colors duration-300"
      style={headerStyle}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">

        {/* Brand */}
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
          <div
            className="h-7 w-7 rounded-sm flex items-center justify-center font-display font-black text-[10px]"
            style={{ backgroundColor: theme.color, color: "#000" }}
          >
            3·6·9
          </div>
          <div className="flex flex-col leading-none">
            <span className={`font-display font-black text-base tracking-tight ${isDark ? "text-white" : "text-black"}`}>
              GauriLabs
            </span>
            <span className="text-[9px] tracking-[0.3em] uppercase text-zinc-500">Power Execution</span>
          </div>
        </Link>

        {/* Nav */}
        {variant === "marketing" && (
          <nav className={`hidden md:flex items-center gap-7 text-sm ${textClass}`} data-testid="nav-marketing">
            <a href="#story"   className={`hover:${isDark ? "text-white" : "text-black"} transition-colors`}>The 3-6-9</a>
            <a href="#videos"  className={`hover:${isDark ? "text-white" : "text-black"} transition-colors`}>Videos</a>
            <a href="#pricing" className={`hover:${isDark ? "text-white" : "text-black"} transition-colors`}>Tiers</a>
          </nav>
        )}

        <div className="flex items-center gap-2">

          {/* ── Dark / Light / System toggle ── */}
          <div className="relative">
            <button
              onClick={function () { setModeMenu(function (v) { return !v; }); }}
              className={"inline-flex items-center justify-center w-9 h-9 rounded-full border transition-all " + btnBorder + " " + btnText}
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark
                ? <Sun  className="h-4 w-4" />
                : <Moon className="h-4 w-4" />}
            </button>

            {modeMenu && (
              <div
                className={"absolute right-0 top-11 w-44 border shadow-xl z-50 " + (isDark ? "bg-[#0A0A0A] border-white/10" : "bg-white border-black/8")}
                onMouseLeave={function () { setModeMenu(false); }}
              >
                {[
                  { icon: <Sun  className="h-3.5 w-3.5" />, label: "Light",  action: function () { if (isDark) toggleMode(); setModeMenu(false); } },
                  { icon: <Moon className="h-3.5 w-3.5" />, label: "Dark",   action: function () { if (!isDark) toggleMode(); setModeMenu(false); } },
                  { icon: <Monitor className="h-3.5 w-3.5" />, label: "System", action: function () { resetToSystem(); setModeMenu(false); } },
                ].map(function (item) {
                  return (
                    <button
                      key={item.label}
                      onClick={item.action}
                      className={"w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors " + (isDark ? "text-zinc-300 hover:text-white hover:bg-white/5" : "text-zinc-600 hover:text-black hover:bg-black/5")}
                    >
                      {item.icon} {item.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Credits */}
          <div
            className="hidden sm:flex items-center gap-2 px-3 h-9 rounded-full border bg-transparent"
            style={{ borderColor: theme.color }}
            data-testid="credits-balance-header"
          >
            <Coins className="h-3.5 w-3.5" style={{ color: theme.color }} />
            <span className="text-xs font-mono font-semibold tracking-wide" style={{ color: theme.color }}>
              {user && (user.role === "client" || user.role === "investor" || user.role === "affiliate" || user.role === "user")
                ? user.credits : 50} Credits
            </span>
          </div>

          {/* Role-based nav */}
          {user && (user.role === "client" || user.role === "user") && (
            <Link to="/dashboard" className={"hidden sm:inline-flex items-center gap-1.5 text-sm transition-colors " + btnText} data-testid="nav-dashboard">
              <UserCircle2 className="h-4 w-4" /> Dashboard
            </Link>
          )}
          {user && user.role === "investor" && (
            <Link to="/investor" className={"hidden sm:inline-flex items-center gap-1.5 text-sm transition-colors " + btnText} data-testid="nav-investor">
              <Link2 className="h-4 w-4" /> Investor
            </Link>
          )}
          {user && user.role === "affiliate" && (
            <Link to="/commander/affiliate" className={"hidden sm:inline-flex items-center gap-1.5 text-sm transition-colors " + btnText} data-testid="nav-affiliate">
              <Megaphone className="h-4 w-4" /> Affiliate
            </Link>
          )}
          {user && user.role === "admin" && (
            <Link to="/commander/admin" className={"hidden sm:inline-flex items-center gap-1.5 text-sm transition-colors " + btnText} data-testid="nav-admin">
              <Shield className="h-4 w-4" /> Commander
            </Link>
          )}

          {/* Auth buttons */}
          {user ? (
            <>
              <button
                onClick={function () { setChangePwOpen(true); }}
                className={"inline-flex items-center gap-1.5 text-sm px-3 h-9 rounded-full border transition " + btnBorder + " " + btnText}
                data-testid="change-password-btn"
              >
                <KeyRound className="h-3.5 w-3.5" />
                <span className="hidden md:inline">Password</span>
              </button>
              <button
                onClick={handleLogout}
                className={"inline-flex items-center gap-1.5 text-sm px-3 h-9 rounded-full border transition " + btnBorder + " " + btnText}
                data-testid="logout-btn"
              >
                <LogOut className="h-3.5 w-3.5" /> Logout
              </button>
            </>
          ) : (
            <Link to="/login"
              className={"text-sm px-3 h-9 inline-flex items-center rounded-full border transition " + btnBorder + " " + btnText}
              data-testid="nav-login">
              Login
            </Link>
          )}
        </div>
      </div>

      <ChangePasswordModal open={changePwOpen} onClose={function () { setChangePwOpen(false); }} />
    </header>
  );
}
